import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { X, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "../../ui/alert";
import { Button } from "@/components/ui/button";
import { handleFileUpload } from "@/api/filesApi";
import { createCourse, updateCourse } from "@/api/courseApi";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const courseInfoSchema = z.object({
  title: z
    .string()
    .min(1, "Course title is required")
    .min(5, "Course title must be at least 5 characters")
    .max(100, "Course title must not exceed 100 characters"),
  description: z
    .string()
    .min(1, "Course description is required")
    .min(10, "Course description must be at least 10 characters")
    .max(1000, "Course description must not exceed 1000 characters"),
  coverImage: z.any().optional(),
  tags: z
    .array(z.string())
    .min(1, "At least one tag is required")
    .optional()
    .or(z.literal(undefined)),
});

const CourseInformationStep = ({
  mode = "ADD",
  onDataChange,
  setCurrentStep,
  initialData = null,
  isReadOnly = false,
}) => {
  const savedCourseData = localStorage.getItem("courseData");
  const parsedSavedData = savedCourseData ? JSON.parse(savedCourseData) : {};
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(courseInfoSchema),
    defaultValues: {
      title: mode !== "ADD" ? (initialData?.title || parsedSavedData.name ): "",
      description: mode !== "ADD" ? (initialData?.description || parsedSavedData.description) : "",
      coverImage: mode !== "ADD" ? (initialData?.coverImage || parsedSavedData.coverImage) : null,
      tags: mode !== "ADD" ? (initialData?.tags || parsedSavedData.tags) : [],
    },
    mode: "onChange",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setValue("title", initialData.title);
      setValue("description", initialData.description);
      setValue("coverImage", initialData.coverImage);
      setValue("tags", initialData.tags);
    } else {
      // Try to load from localStorage if no initialData
      const savedCourseData = localStorage.getItem("courseData");
      if (savedCourseData) {
        try {
          const parsed = JSON.parse(savedCourseData);
          if (parsed.name) {
            setValue("title", parsed.name);
            setValue("description", parsed.description || "");
            setValue("coverImage", parsed.coverImage || null);
            setValue("tags", parsed.tags || []);
            
            // Update local state
            setCoverImage(parsed.coverImage || null);
            setTagInput("");
          }
        } catch (error) {
          console.warn("Failed to parse saved course data:", error);
        }
      }
    }
  }, [initialData, setValue]);

  // Local state for UI management
  const [tagInput, setTagInput] = useState(initialData?.tags ?? "");
  const [imageError, setImageError] = useState("");
  const [coverImage, setCoverImage] = useState({url : initialData?.coverImage ? import.meta.env.VITE_CDN_LINK + initialData.coverImage : null });
  const formValues = watch();
  // Notify parent of data changes when form values change
  useEffect(() => {
    if (onDataChange) {
      onDataChange(formValues);
    }
  }, [formValues, onDataChange]);

  // Sync local coverImage state with form value
  useEffect(() => {
    setCoverImage({url : formValues.coverImage ? import.meta.env.VITE_CDN_LINK + formValues.coverImage : null });
  }, [formValues.coverImage]);

  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];

    setImageError("");

    if (!file) {
      console.log("No file selected, clearing image");
      setCoverImage(null);
      setValue("coverImage", null);
      return;
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = `Unsupported file type: ${file.type}. Please select a valid image file (JPG, PNG, GIF, WebP)`;
      console.error("Invalid file type:", errorMsg);
      setImageError(errorMsg);
      e.target.value = ""; // Clear the input
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

    if (file.size > maxSize) {
      const errorMsg = `File too large: ${fileSizeMB}MB (max 5MB)`;
      setImageError(errorMsg);
      e.target.value = ""; // Clear the input
      return;
    }

    const imageData = {
      file: file,
      fileType: "IMAGE",
      fileCategory: "GENERAL",
      url: URL.createObjectURL(file),
      lastModified: file.lastModified,
      size: file.size,
      type: file.type,
    };

    setCoverImage(imageData);
    setValue("coverImage", imageData, { shouldValidate: true });
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      const currentTags = formValues.tags || [];
      const newTag = tagInput.trim().toLowerCase();

      // Validate tag
      if (newTag.length < 2) {
        return; // Too short
      }

      if (newTag.length > 20) {
        return; // Too long
      }

      if (!currentTags.map((tag) => tag.toLowerCase()).includes(newTag)) {
        setValue("tags", [...currentTags, tagInput.trim()]);
      }
      setTagInput("");
    }
  };

  const handleCourseInfoSubmit = async (data) => {
    setLoading(true);

    let storedCourseData = localStorage.getItem("courseData");
    let parsedCourseData = storedCourseData ? JSON.parse(storedCourseData) : {};
    let courseId = parsedCourseData?.courseId;

    // Upload cover image if new file is provided
    if (data.coverImage && data.coverImage.file instanceof File) {
      const fileData = data.coverImage.file;
      const fileUpload = await handleFileUpload(fileData);

      if (fileUpload.success) {
        const fileUploadId = fileUpload?.fileUploadId;
        data.coverImageId = fileUploadId;
        const coverImage = import.meta.env.VITE_CDN_LINK + fileUpload.uploadKey;
        setCoverImage(coverImage);
      }
    }

    let response;

    if (courseId) {
      // UPDATE COURSE
      response = await updateCourse(courseId, {
        name: data.title,
        description: data.description,
        coverImageId: data.coverImageId,
        tags: data.tags,
        isActive: false,
      });
    } else {
      // CREATE COURSE
      response = await createCourse({
        name: data.title,
        description: data.description,
        coverImageId: data.coverImageId,
        tags: data.tags,
        isActive: false,
      });
    }

    if (response.success) {
      courseId = response.data.id;

      const localStoreData = {
        name: response.data.name,
        description: response.data.description,
        coverImage: response.data.coverImage,
        tags: response.data.tags,
        isActive: false,
        courseId,
      };

      localStorage.setItem("courseData", JSON.stringify(localStoreData));
      sessionStorage.setItem("courseId", courseId);

      toast.success(
        courseId ? "Course updated successfully" : "Course created successfully"
      );
      setCurrentStep((prev) => prev + 1);
    } else {
      console.error("Failed to save course:", response.error);
    }

    setLoading(false);
  };

  const removeTag = (tagToRemove) => {
    const currentTags = formValues.tags || [];
    const newTags = currentTags.filter((tag) => tag !== tagToRemove);
    setValue("tags", newTags);
  };

  const removeCoverImage = () => {
    setCoverImage(null);
    setValue("coverImage", null);
    setImageError("");
  };

  // Method to validate and get current form data
  const validateAndGetData = () => {
    return new Promise((resolve, reject) => {
      handleSubmit(
        (data) => resolve(data),
        (errors) => reject(errors)
      )();
    });
  };

  // Expose validation method to parent if needed
  useEffect(() => {
    if (onDataChange) {
      // You can extend this to pass validation method to parent
      onDataChange({ ...formValues, validate: validateAndGetData });
    }
  }, [formValues]);

  const disableAll = loading || mode === "VIEW";

  return (
    <div className="w-full h-full relative">
      <div className="w-full h-full space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Course Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Course Title *</Label>
              <Input
                id="title"
                placeholder="Enter course title (minimum 5 characters)"
                {...register("title")}
                className={`${disableAll ? "mt-1" : errors.title ? "border-red-500 mt-1" : "mt-1"}`}
                disabled={disableAll}
                maxLength={100}
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title.message}</p>
              )}
              <div className="flex justify-between">
                <p className="text-xs text-gray-500">
                  A clear, descriptive title for your course
                </p>
                <p className="text-xs text-gray-400">
                  {formValues.title?.length || 0}/100
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Course Tags *</Label>
              <div className="flex flex-wrap gap-2 mb-2 min-h-[2rem]">
                {(formValues.tags || []).map((tag) => (
                  <div
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-[var(--secondary-font)]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-[var(--primary-font)] hover:text-[var(--secondary-font)] focus:outline-none"
                      disabled={mode === "VIEW"}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {(formValues.tags || []).length === 0 && (
                  <div className="text-sm text-gray-400 py-1">
                    No tags added yet
                  </div>
                )}
              </div>
              <Input
                id="tags"
                placeholder="Enter tags and press Enter (e.g., navigation, safety, stcw)"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagKeyDown}
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    handleTagKeyDown({ ...e, key: "Enter" });
                  }
                }}
                disabled={disableAll}
                className={
                  !formValues.tags || formValues.tags.length === 0
                    ? "border-red-300"
                    : ""
                }
                maxLength={20}
              />
              <p className="text-sm text-gray-500">
                Add tags to help students find your course. Press Enter or comma
                to add each tag. (2-20 characters each)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Course Description *</Label>
              <Textarea
                id="description"
                placeholder="Enter a detailed course description (minimum 10 characters)"
                {...register("description")}
                className={`${mode === "VIEW" ? "mt-1" : errors.description ? "border-red-500 mt-1" : "mt-1"}`}
                rows={mode === "VIEW" ? 4 : 6}
                disabled={mode === "VIEW"}
                maxLength={1000}
              />
              {errors.description && (
                <p className="text-sm text-red-500">
                  {errors.description.message}
                </p>
              )}
              <div className="flex justify-between">
                <p className="text-sm text-gray-500">
                  Provide a comprehensive overview of what students will learn
                </p>
                <p className="text-xs text-gray-400">
                  {formValues.description?.length || 0}/1000
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Course Cover Image</Label>
              {imageError && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{imageError}</AlertDescription>
                </Alert>
              )}
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center w-full">
                  {coverImage?.url ? (
                    <div className="relative">
                      <img
                        src={coverImage.url}
                        alt="Course cover preview"
                        className="mx-auto h-48 w-full object-contain rounded-md border"
                      />
                      {mode !== "VIEW" && (
                        <button
                          type="button"
                          onClick={removeCoverImage}
                          className="absolute -top-2 -right-2 bg-neutral-400 text-white rounded p-1 hover:bg-red-700 focus:outline-none shadow-lg transition-all"
                          aria-label="Remove image"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                      <div className="mt-2 text-center">
                        <p className="text-sm text-gray-600">
                          {coverImage.file?.name || "Cover image uploaded"}
                        </p>
                        {coverImage.file?.size && (
                          <p className="text-xs text-gray-400">
                            Size:{" "}
                            {(coverImage.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex flex-col items-center justify-center gap-2">
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF, WebP up to 5MB
                        </p>
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-[var(--primary-font)] hover:text-[var(--primary-font)] focus-within:outline-none"
                          >
                            <span>Upload an image</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                              onChange={handleCoverImageChange}
                              accept="image/*"
                              disabled={mode === "VIEW"}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {mode !== "VIEW" && (
        <div className="flex justify-end mt-8">
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => {
              handleSubmit(
                (data) => {
                  handleCourseInfoSubmit(data);
                },
                (errors) => {
                  console.log("Form has errors:", errors);
                }
              )();
            }}
          >
            {loading ? <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> 
            Saving
            </>
            : "Save & Next"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CourseInformationStep;
